import React, { useEffect, useState } from 'react';
import axios from 'axios';
import styles from './TransactionModal.module.css';
import { FIND_TRANSACTION_BILL_NO, UPDATE_TRANSACTION_BILL_NO } from '../constant/apiConstants';

const TransactionModal = ({ handleClose, billNo, totalAmount }) => {
  const [transactions, setTransactions] = useState([]);
  const [editedTransactions, setEditedTransactions] = useState([]);
  const [error, setError] = useState('');
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [storeId, setStoreId] = useState(null);

  useEffect(() => {
    const getUserData = () => {
      const storedUser = sessionStorage.getItem('user');
      const storedToken = sessionStorage.getItem('token');
      return {
        user: storedUser ? JSON.parse(storedUser) : null,
        token: storedToken || null,
      };
    };

    const { user: userData, token: userToken } = getUserData();

    if (userData) {
      setUser(userData);
      setToken(userToken);
      setStoreId(userData.storeId);
    }
  }, []);

  useEffect(() => {
    const fetchTransactions = async () => {
      if (billNo && storeId) {
        try {
          const response = await axios.post(FIND_TRANSACTION_BILL_NO, {
            billNo,
            storeId,
            user,
            token,
          });

          if (response.data.status === 'success') {
            const data = response.data.payload;
            setTransactions(data);
            setEditedTransactions(data);
          }
        } catch (error) {
          console.error('Error fetching transactions:', error);
        }
      }
    };

    fetchTransactions();
  }, [billNo, storeId]);

  const sumOfAmounts = editedTransactions.reduce((acc, txn) => acc + (txn.amount || 0), 0);

  // Amount of the bill still left to allocate across payment modes.
  const remaining = Number(totalAmount) - sumOfAmounts;
  const isBalanced = Math.abs(remaining) < 0.005;

  const handleInputChange = (index, field, value) => {
    const updated = [...editedTransactions];
    updated[index][field] = value;
    setEditedTransactions(updated);
    setError('');
  };

  const handleAddTransaction = () => {
    if (editedTransactions.length >= 3) {
      setError('Only one transaction per mode (CASH, CARD, UPI) is allowed.');
      return;
    }

    const newTransaction = {
      transactionId: null,
      description: '',
      mode: '',
      amount: 0,
      transactionType: 'DEBIT',
      isNew: true,
    };
    setEditedTransactions([...editedTransactions, newTransaction]);
    setError('');
  };

  const handleSave = async () => {
    if (!isBalanced) {
      setError(`Sum of all transaction amounts (${sumOfAmounts}) must equal the total amount (${totalAmount}).`);
      return;
    }

    try {
      const transactionsList = editedTransactions
        .filter((txn) => {
          const isNew = txn.transactionId === null || txn.isNew || txn.transactionId?.toString().startsWith('new');
          return !isNew || (isNew && txn.amount > 0);
        })
        .map((txn) => {
          const isNew = txn.transactionId === null || txn.isNew || txn.transactionId?.toString().startsWith('new');

          const mapped = {
            billNo,
            storeId,
            mode: txn.mode,
            amount: txn.amount,
            status: isNew ? 'NEW' : 'EDITED',
          };

          if (!isNew) {
            mapped.transactionId = txn.transactionId;
          }

          return mapped;
        });

      const response = await axios.post(UPDATE_TRANSACTION_BILL_NO, {
        user,
        token,
        storeId,
        billNo,
        transactionsList,
      });

      alert(response.data.message);
      handleClose();
    } catch (err) {
      console.error(err);
      alert('Error saving transactions.');
    }
  };

  const availableModes = (index) => {
    const usedModes = editedTransactions
      .map((t, i) => (i !== index ? t.mode : null))
      .filter(Boolean);

    return ['CASH', 'CARD', 'UPI'].filter((mode) => !usedModes.includes(mode));
  };

  return (
    <div className={styles.overlay}>
      <div className={styles.container}>
        <div className={styles.header}>
          <h2>Transaction Details</h2>
          <h3>Bill No: <strong>{billNo}</strong></h3>
          <h3>Total: <strong>{totalAmount}</strong></h3>
          <button className={styles.closeButton} onClick={handleClose}>&times;</button>
        </div>

        <div className={styles.body}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Payment Mode</th>
                <th>Amount</th>
              </tr>
            </thead>
            <tbody>
              {editedTransactions.map((txn, index) => (
                <tr key={txn.transactionId || `new-${index}`}>
                  <td>
                    <select
                      value={txn.mode}
                      onChange={(e) => handleInputChange(index, 'mode', e.target.value)}
                    >
                      <option value="">Select Mode</option>
                      {availableModes(index).map((mode) => (
                        <option key={mode} value={mode}>{mode}</option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <input
                      type="number"
                      min="0"
                      value={txn.amount}
                      onChange={(e) => {
                        const val = parseFloat(e.target.value);
                        handleInputChange(index, 'amount', isNaN(val) ? 0 : val);
                      }}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <button className={`${styles.btn} ${styles.add}`} onClick={handleAddTransaction}>
            + Add Transaction
          </button>

          {error && <p className={styles.error}>{error}</p>}

          <div className={styles.totals}>
            <p className={styles.totalLine}>
              <span>Total Amount</span>
              <span style={{ color: '#2563eb' }}>{totalAmount}</span>
            </p>
            
            <p className={styles.totalLine}>
              <span>Amount</span>
              <span style={{ color: '#16a34a' }}>{sumOfAmounts}</span>
            </p>
            <p className={styles.remaining}>
              <span>{remaining < 0 ? 'Over by' : 'Remaining'}</span>
              <span style={{ color: '#dc2626' }}>{Math.abs(remaining)}</span>
            </p>
          </div>
        </div>

        <div className={styles.footer}>
          <button className={`${styles.btn} ${styles.cancel}`} onClick={handleClose}>Close</button>
          <button
            className={`${styles.btn} ${styles.save}`}
            onClick={handleSave}
            disabled={!isBalanced}
            title={!isBalanced ? 'Sum of transaction amounts must equal total amount' : ''}
          >
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
};

export default TransactionModal;